import { FRAME_INSET, type Size } from './printLayout'
import type { PrintProjector } from './printProjection'
import { DEFAULT_PRINT_MARGIN_MM, snapPrintScale } from './printScale'
import { pageForAspect, type PageMm } from './printCustomSheet'
import type { GeoPt } from './types'

interface ViewportArgs {
  projector: PrintProjector
  /** The map's own screen-to-lon/lat transform at the current zoom. */
  unproject: (xy: [number, number]) => GeoPt
  containerPx: Size
  inset?: number
}

/**
 * The ground the frame-sized part of the viewport covers, in metres.
 *
 * Measured through the projector rather than from the screen's lon/lat span,
 * for the same reason `printFrameCorners` inverts each corner separately: the
 * preview is Web Mercator and the sheet is a national grid, so the same screen
 * rectangle is a DIFFERENT ground rectangle at different latitudes. Deriving a
 * page size from the screen alone would print a southern sheet at a northern
 * one's size.
 *
 * The projected bounding box is then divided back into GROUND metres, which is
 * what every caller below wants and what the name has always promised. For a
 * national grid that division is by one and nothing moves. For the worldwide
 * Mercator fallback it is the whole difference between a sensible page and one
 * twice too large: measured in Mercator's own metres, a window over Berlin
 * reads as half again more ground than it holds, and over Trondheim as double.
 *
 * The factor is taken at the view's centre latitude, the same place the sheet
 * takes its own (see `crsMetresPerGroundMetre`) — so the two quantities the
 * callers below compare are converted by the same number, and a residual in it
 * cannot make a sheet look like it fits a view it does not.
 */
export function viewportGroundExtentM({
  projector, unproject, containerPx, inset = FRAME_INSET,
}: ViewportArgs): { w: number; h: number } {
  const halfW = (containerPx.w * inset) / 2
  const halfH = (containerPx.h * inset) / 2
  const cx = containerPx.w / 2
  const cy = containerPx.h / 2

  const corners: [number, number][] = [
    [cx - halfW, cy - halfH], [cx + halfW, cy - halfH],
    [cx + halfW, cy + halfH], [cx - halfW, cy + halfH],
  ]
  const projected = corners.map((px) => projector.forward(unproject(px)))
  const es = projected.map(([e]) => e)
  const ns = projected.map(([, n]) => n)
  const k = projector.crsMetresPerGroundMetre(unproject([cx, cy]).lat)
  return {
    w: (Math.max(...es) - Math.min(...es)) / k,
    h: (Math.max(...ns) - Math.min(...ns)) / k,
  }
}

/**
 * The page that fits the view at a fixed scale and a fixed aspect.
 *
 * `aspect` describes the TOTAL page — the same quantity `pageAspect` reads
 * off a `PageMm` everywhere else in this codebase (A4 portrait is 0.707 of
 * total paper, not of its printable area), because that is the number the
 * custom-size slider drags: the product spec has it starting at "the aspect
 * ratio of the paper size that was chosen" and the page display is
 * documented as "total page size, including the margins". A version of this
 * function that read `aspect` as the MAP box's ratio would make the page's
 * own `pageAspect` disagree with the slider the instant margins were added,
 * which reads as the slider jumping on every drag.
 *
 * The aspect is never changed here. The SIZE is fitted inside the view on its
 * binding axis — but only as far as the 100..2000 mm page range allows, and
 * the range wins where the two disagree: a ratio the user set is not worth
 * trading for a frame that happens to sit on screen. Below the floor the
 * returned page therefore spills past the view, which at 1:50 000 is any view
 * narrower than four kilometres, and the caller is expected to zoom out to it
 * (`usePrintFrame`'s `fitWhenSheetOverflows`).
 */
export function pageForViewport({
  scale, aspect, ...view
}: ViewportArgs & { scale: number; aspect: number }): PageMm {
  const ground = viewportGroundExtentM(view)
  const margin = 2 * DEFAULT_PRINT_MARGIN_MM
  // The MAP area is what has to fit the view, so the view's limit is a limit
  // on the map box; the page is that plus its margins. Working in total-page
  // space from here keeps `aspect` meaning the same thing it means
  // everywhere else — and lets `pageForAspect` do the range clamping, which
  // preserves the ratio rather than truncating one edge of it the way a
  // clamp applied after the fact would.
  const maxTotalW = (ground.w * 1000) / scale + margin
  const maxTotalH = (ground.h * 1000) / scale + margin
  // Rounded up before deriving the other edge from it: `pageForAspect`'s own
  // rounding (via `clampPage`) is to the NEAREST millimetre, which can shave
  // up to 0.5mm off the derived edge — an acceptable sub-millimetre
  // tolerance — but the anchor edge computed here is rounded up so the page
  // this function controls directly never ends up smaller than the view.
  const totalH = Math.ceil(Math.min(maxTotalH, maxTotalW / aspect))
  return pageForAspect({ w: totalH * aspect, h: totalH }, aspect)
}

/**
 * The scale at which a fixed page fits INSIDE the view.
 *
 * The same rule `pageForViewport` follows, and for the same reason: the frame
 * is the thing the hunter composes against, so it has to be on screen with
 * room around it. Both halves of that are deliberate and both were once the
 * other way.
 *
 * The binding axis is the one that runs out FIRST (`min`), not the one that
 * runs out last. Sizing the sheet to cover the view instead means that
 * whenever the page's shape differs from the screen's — which is almost
 * always — the sheet must spill off the other axis by the whole ratio between
 * them, unboundedly: a 442 x 625 mm sheet on a 1200 x 800 map measured
 * 1473 x 2112 px, every edge off screen and nothing shading the surround.
 *
 * And it snaps FINER, so the step itself cannot put back what the fit just
 * took away; the coarser snap could inflate the frame by half as much again.
 * The cost of both is that the sheet covers a little less ground than the
 * window shows, which is the same trade `pageForViewport` makes and is
 * visible on screen rather than discovered on paper.
 */
export function scaleForViewport({
  page, ...view
}: ViewportArgs & { page: PageMm }): number {
  const ground = viewportGroundExtentM(view)
  const printable = {
    w: Math.max(1, page.w - 2 * DEFAULT_PRINT_MARGIN_MM),
    h: Math.max(1, page.h - 2 * DEFAULT_PRINT_MARGIN_MM),
  }
  const fits = Math.min((ground.w * 1000) / printable.w, (ground.h * 1000) / printable.h)
  return snapPrintScale(fits, 'finer')
}

/**
 * Whether a page's ground is more than the view can hold.
 *
 * Both sides are measured through the SHEET'S OWN CRS, and that is the entire
 * point. The obvious alternative — fit the frame's corners on screen and see
 * whether they leave the container — compares two quantities taken in different
 * planes,
 * and the error does not cancel: away from the projection's central meridian
 * grid north is not screen north, so `viewportGroundExtentM`'s bounding box in
 * the CRS is bigger than the view by the convergence angle, and the frame's
 * bounding box on SCREEN is bigger than the frame by the same angle again. A
 * page sized to an over-measured view, judged against an over-measured frame,
 * overflows by a fixed fraction at every zoom — so each fit derives a
 * proportionally larger page that overflows again. Measured through the real
 * EPSG:3067 projector, a custom sheet over Helsinki walked the map out 42
 * zoom steps, ending at the largest and most expensive sheet the UI can
 * express. Measuring both sides here, both carry the same inflation and it
 * cancels.
 *
 * What is left over is a frame up to a few per cent outside the container, and
 * that is harmless: `FRAME_INSET` fits the sheet to 88% of the map, so there is
 * 13.6% of headroom before it reaches an edge at all.
 */
export function pageExceedsViewport({
  page, scale, ...view
}: ViewportArgs & { page: PageMm; scale: number }): boolean {
  const ground = viewportGroundExtentM(view)
  const printable = {
    w: Math.max(1, page.w - 2 * DEFAULT_PRINT_MARGIN_MM),
    h: Math.max(1, page.h - 2 * DEFAULT_PRINT_MARGIN_MM),
  }
  // A per cent of slack, so the page's whole-millimetre rounding cannot read as
  // an overflow on a sheet that was derived from this very view.
  return (
    (printable.w * scale) / 1000 > ground.w * 1.01 ||
    (printable.h * scale) / 1000 > ground.h * 1.01
  )
}
